class MediaCenterSearch {
  constructor(root) {
    this.root = root;
    this.index = [];
    this.query = '';
    this.filter = 'all';
    this.page = 1;
    this.previewLimit = 3;
    this.pageSize = 10;
    this.ready = false;

    const dataEl = document.getElementById('media-center-search-data');
    if (dataEl) {
      try {
        this.index = JSON.parse(dataEl.textContent);
      } catch (error) {
        this.index = [];
      }
    }

    const sourcesEl = document.getElementById('media-center-search-sources');
    if (sourcesEl) {
      try {
        this.sources = JSON.parse(sourcesEl.textContent);
        this.previewLimit = Number(this.sources.previewLimit) || 3;
        this.pageSize = Number(this.sources.pageSize) || 10;
      } catch (error) {
        this.sources = {};
      }
    } else {
      this.sources = {};
    }

    this.form = root.querySelector('[data-media-center-search-form]');
    this.input = root.querySelector('[data-media-center-search-input]');
    this.resultsEl = root.querySelector('[data-media-center-search-results]');
    this.summaryEl = root.querySelector('[data-media-center-search-summary]');
    this.titleEl = root.querySelector('[data-mc-search-title]');
    this.emptyEl = root.querySelector('[data-media-center-search-empty]');
    this.filterInputs = root.querySelectorAll('[data-media-center-filter]');

    this.init();
  }

  async init() {
    const params = new URLSearchParams(window.location.search);
    this.query = (params.get('q') || '').trim();
    this.filter = params.get('type') || 'all';
    this.page = Math.max(1, Number(params.get('page')) || 1);

    if (this.input) {
      this.input.value = this.query;
    }

    this.filterInputs.forEach((input) => {
      input.checked = input.value === this.filter;
      input.addEventListener('change', () => {
        if (input.checked) {
          this.filter = input.value;
          this.page = 1;
          this.updateUrl();
          this.render();
        }
      });
    });

    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.query = this.input.value.trim();
        this.page = 1;
        this.updateUrl();
        this.render();
      });
    }

    this.resultsEl?.addEventListener('click', (event) => {
      const viewAllKits = event.target.closest('[data-mc-view-all-kits]');
      if (viewAllKits) {
        event.preventDefault();
        this.setFilter('kit');
        return;
      }

      const viewAllNews = event.target.closest('[data-mc-view-all-news]');
      if (viewAllNews) {
        event.preventDefault();
        this.setFilter('news');
        return;
      }

      const pageBtn = event.target.closest('[data-mc-search-page]');
      if (pageBtn) {
        event.preventDefault();
        const nextPage = Number(pageBtn.getAttribute('data-mc-search-page'));
        if (Number.isFinite(nextPage) && nextPage > 0) {
          this.page = nextPage;
          this.updateUrl();
          this.render();
          this.resultsEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    });

    this.render();
    await this.loadRemoteKits();
    this.ready = true;
    this.render();
  }

  setFilter(value) {
    this.filter = value;
    this.page = 1;
    this.filterInputs.forEach((input) => {
      input.checked = input.value === value;
    });
    this.updateUrl();
    this.render();
  }

  async loadRemoteKits() {
    const collectionPage = this.sources?.collectionPage;
    if (!collectionPage) return;

    try {
      const doc = await MediaCenter.fetchDocument(collectionPage);
      const remoteKits = [];

      doc.querySelectorAll('[data-mc-search-kits]').forEach((el) => {
        try {
          const items = JSON.parse(el.textContent || '[]');
          if (Array.isArray(items)) remoteKits.push(...items);
        } catch (error) {
          // Ignore malformed section payloads.
        }
      });

      if (!remoteKits.length) return;

      const existingKeys = new Set(
        this.index
          .filter((item) => item.resultType === 'kit')
          .map((item) => `${this.normalize(item.title)}|${this.normalize(item.url)}`)
      );

      remoteKits.forEach((item) => {
        const key = `${this.normalize(item.title)}|${this.normalize(item.url)}`;
        if (!existingKeys.has(key)) {
          this.index.push(item);
          existingKeys.add(key);
        }
      });
    } catch (error) {
      // Keep embedded index when remote page cannot be loaded.
    }
  }

  updateUrl() {
    const url = new URL(window.location.href);
    if (this.query) {
      url.searchParams.set('q', this.query);
    } else {
      url.searchParams.delete('q');
    }

    if (this.filter && this.filter !== 'all') {
      url.searchParams.set('type', this.filter);
    } else {
      url.searchParams.delete('type');
    }

    if (this.filter === 'kit' && this.page > 1) {
      url.searchParams.set('page', String(this.page));
    } else {
      url.searchParams.delete('page');
    }

    window.history.replaceState({}, '', url);
  }

  normalize(value) {
    return (value || '').toString().toLowerCase();
  }

  matches(item) {
    if (!this.query) return false;

    const haystack = [
      item.title,
      item.category,
      item.collection,
      item.type,
      item.tag,
      item.excerpt,
      item.keywords
    ].join(' ');

    return this.normalize(haystack).includes(this.normalize(this.query));
  }

  getMatchedResults() {
    if (!this.query) return [];
    return this.index.filter((item) => this.matches(item));
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  decodeEntities(value) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value || '');
    return textarea.value;
  }

  formatTemplate(template, replacements = {}) {
    let output = this.decodeEntities(template || '');
    Object.entries(replacements).forEach(([key, value]) => {
      output = output.split(`{{ ${key} }}`).join(String(value ?? ''));
    });
    return output;
  }

  renderKitCard(item) {
    const zipIcon = `<div class="media-center-kit-card__preview media-center-kit-card__preview--zip" aria-hidden="true"><span class="media-center-kit-card__icon-hold"><svg width="28" height="28" viewBox="0 0 32 32" fill="none" aria-hidden="true"><rect x="2" y="4" width="20" height="16" rx="1.5" stroke="currentColor" stroke-width="1.5"/><circle cx="8" cy="9" r="1.5" fill="currentColor"/><path d="M4.5 16.5 9 12l3 3 4-5 5.5 6.5" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/><rect x="10" y="10" width="20" height="16" rx="1.5" stroke="currentColor" stroke-width="1.5" fill="var(--mc-surface, #f7f5f3)"/><circle cx="16" cy="15" r="1.5" fill="currentColor"/><path d="M12.5 22.5 17 18l3 3 4-5 5.5 6.5" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg></span></div>`;

    const previews = (item.previews || []).filter(Boolean).slice(0, 3).map((preview) => {
      return `<div class="media-center-kit-card__preview"><img src="${preview}" alt="" loading="lazy" width="200" height="150"></div>`;
    });

    if (item.zipUrl) {
      previews.push(zipIcon);
    }

    const sizeAttr = item.zipUrl ? ` data-mc-zip-url="${this.escapeHtml(item.zipUrl)}"` : '';
    const sizeHtml = item.fileSize
      ? `<span class="media-center-kit-card__size" data-mc-kit-size${sizeAttr}>${this.escapeHtml(item.fileSize)}</span>`
      : `<span class="media-center-kit-card__size" data-mc-kit-size${sizeAttr} hidden></span>`;

    return `
      <a class="media-center-kit-card" href="${this.escapeHtml(item.url)}">
        <div class="media-center-kit-card__header">
          <h3 class="media-center-kit-card__title">${this.escapeHtml(item.title)}</h3>
          ${sizeHtml}
        </div>
        <div class="media-center-kit-card__previews">${previews.join('')}</div>
        <div class="media-center-kit-card__footer">
          <span class="media-center-kit-card__link">${window.mediaCenterStrings?.openMediaKit || 'Open media kit'}</span>
        </div>
      </a>
    `;
  }

  renderNewsCard(item) {
    const image = item.preview
      ? `<div class="media-center-news-card__media"><img src="${this.escapeHtml(item.preview)}" alt="" loading="lazy" width="400" height="280"></div>`
      : '';
    const tag = item.tag
      ? `<span class="media-center-news-card__tag">${this.escapeHtml(item.tag)}</span>`
      : '';

    return `
      <a class="media-center-news-card" href="${this.escapeHtml(item.url)}">
        ${image}
        <div class="media-center-news-card__body">
          <div class="media-center-news-card__meta">
            ${item.date ? `<time>${this.escapeHtml(item.date)}</time>` : ''}
            ${tag}
          </div>
          <h3 class="media-center-news-card__title">${this.escapeHtml(item.title)}</h3>
          ${item.excerpt ? `<p class="media-center-news-card__excerpt">${this.escapeHtml(item.excerpt)}</p>` : ''}
        </div>
      </a>
    `;
  }

  updatePageTitle() {
    if (!this.titleEl) return;

    let title = window.mediaCenterStrings?.titleAll || 'Search results';
    if (this.filter === 'news') {
      title = window.mediaCenterStrings?.titleNews || 'Search news releases';
    } else if (this.filter === 'kit') {
      title = window.mediaCenterStrings?.titleKits || 'Search media kits';
    }

    this.titleEl.textContent = this.decodeEntities(title);
    if (!document.querySelector('[data-media-center-router]:not(.is-mounted)')) {
      document.title = `${title} · Media Center`;
    }
  }

  updateFilterLabels(newsCount, kitCount) {
    const allLabel = this.root.querySelector('[data-mc-filter-label="all"]');
    const newsLabel = this.root.querySelector('[data-mc-filter-label="news"]');
    const kitLabel = this.root.querySelector('[data-mc-filter-label="kit"]');

    if (allLabel) {
      allLabel.textContent = window.mediaCenterStrings?.filterAll || 'Everything';
    }
    if (newsLabel) {
      const base = window.mediaCenterStrings?.filterNews || 'News releases';
      newsLabel.textContent = this.query ? `${base} (${newsCount})` : base;
    }
    if (kitLabel) {
      const base = window.mediaCenterStrings?.filterKits || 'Media kits';
      kitLabel.textContent = this.query ? `${base} (${kitCount})` : base;
    }
  }

  renderPagination(totalPages) {
    if (totalPages <= 1) return '';

    const prevDisabled = this.page <= 1;
    const nextDisabled = this.page >= totalPages;
    const pageLabel = (window.mediaCenterStrings?.pageOf || '{{ current }} of {{ total }}')
      .replace('{{ current }}', String(this.page))
      .replace('{{ total }}', String(totalPages));

    return `
      <nav class="media-center-search-pagination" aria-label="Pagination">
        <button type="button" class="media-center-search-pagination__btn" data-mc-search-page="${this.page - 1}" ${prevDisabled ? 'disabled' : ''} aria-label="Previous page">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M10 3 5 8l5 5" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        <span class="media-center-search-pagination__status">${pageLabel}</span>
        <button type="button" class="media-center-search-pagination__btn" data-mc-search-page="${this.page + 1}" ${nextDisabled ? 'disabled' : ''} aria-label="Next page">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m6 3 5 5-5 5" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
      </nav>
    `;
  }

  renderSectionHeader(title, count, showingText) {
    return `
      <div class="media-center-search-section__header">
        <h2 class="media-center-search-section__title">
          ${this.escapeHtml(title)}
          <span class="media-center-kit-card__size">${count}</span>
        </h2>
        ${showingText ? `<p class="media-center-search-section__showing">${this.escapeHtml(showingText)}</p>` : ''}
      </div>
    `;
  }

  render() {
    const matched = this.getMatchedResults();
    const news = matched.filter((item) => item.resultType === 'news');
    const kits = matched.filter((item) => item.resultType === 'kit');
    const total = news.length + kits.length;

    this.updateFilterLabels(news.length, kits.length);
    this.updatePageTitle();

    if (this.summaryEl) {
      if (!this.query) {
        this.summaryEl.textContent = window.mediaCenterStrings?.searchPrompt || 'Enter a search term to browse media kits and assets.';
      } else if (total === 0) {
        this.summaryEl.textContent = this.formatTemplate(
          window.mediaCenterStrings?.noResults || 'No results found for: {{ query }}',
          { query: `"${this.query}"` }
        );
      } else if (this.filter === 'kit') {
        this.summaryEl.textContent = this.formatTemplate(
          window.mediaCenterStrings?.kitsFound || '{{ count }} Media kits found for: {{ query }}',
          { count: kits.length, query: `"${this.query}"` }
        );
      } else if (this.filter === 'news') {
        this.summaryEl.textContent = this.formatTemplate(
          window.mediaCenterStrings?.newsFound || '{{ count }} News releases found for: {{ query }}',
          { count: news.length, query: `"${this.query}"` }
        );
      } else {
        this.summaryEl.textContent = this.formatTemplate(
          window.mediaCenterStrings?.resultsCount || '{{ count }} Search results found for: {{ query }}',
          { count: total, query: `"${this.query}"` }
        );
      }
    }

    if (!this.resultsEl || !this.emptyEl) return;

    if (!this.query) {
      this.resultsEl.innerHTML = '';
      this.emptyEl.hidden = false;
      this.emptyEl.textContent = window.mediaCenterStrings?.searchPrompt || 'Enter a search term to browse media kits and assets.';
      return;
    }

    const visibleNews = this.filter === 'all' || this.filter === 'news' ? news : [];
    const visibleKits = this.filter === 'all' || this.filter === 'kit' ? kits : [];

    if (visibleNews.length === 0 && visibleKits.length === 0) {
      this.resultsEl.innerHTML = '';
      this.emptyEl.hidden = false;
      this.emptyEl.innerHTML = (window.mediaCenterStrings?.noResultsHtml || 'There are no media kits for your search. <a href="#">Try again</a>')
        .replace('{{ query }}', this.query);
      const retry = this.emptyEl.querySelector('a');
      if (retry) {
        retry.addEventListener('click', (event) => {
          event.preventDefault();
          this.query = '';
          this.page = 1;
          if (this.input) {
            this.input.value = '';
            this.input.focus();
          }
          this.updateUrl();
          this.render();
        });
      }
      return;
    }

    this.emptyEl.hidden = true;
    let html = '';

    if (visibleNews.length > 0) {
      const showing = (window.mediaCenterStrings?.showingOf || 'Showing {{ shown }} of {{ total }}')
        .replace('{{ shown }}', String(visibleNews.length))
        .replace('{{ total }}', String(news.length));
      html += `<section class="media-center-search-section">`;
      html += this.renderSectionHeader(window.mediaCenterStrings?.newsReleases || 'News releases', news.length, showing);
      html += `<div class="media-center-search-news">${visibleNews.map((item) => this.renderNewsCard(item)).join('')}</div>`;
      html += `</section>`;
    }

    if (visibleKits.length > 0) {
      const isKitsTab = this.filter === 'kit';
      const totalPages = Math.max(1, Math.ceil(kits.length / this.pageSize));
      if (this.page > totalPages) this.page = totalPages;

      let kitItems = kits;
      let showingCount = kits.length;

      if (isKitsTab) {
        const start = (this.page - 1) * this.pageSize;
        kitItems = kits.slice(start, start + this.pageSize);
        showingCount = kitItems.length;
      } else {
        kitItems = kits.slice(0, this.previewLimit);
        showingCount = kitItems.length;
      }

      const showing = (window.mediaCenterStrings?.showingOf || 'Showing {{ shown }} of {{ total }}')
        .replace('{{ shown }}', String(showingCount))
        .replace('{{ total }}', String(kits.length));

      html += `<section class="media-center-search-section">`;
      html += this.renderSectionHeader(
        window.mediaCenterStrings?.mediaKits || 'Media kits',
        kits.length,
        showing
      );
      html += `<div class="media-center__grid media-center__grid--kits">${kitItems.map((item) => this.renderKitCard(item)).join('')}</div>`;

      if (!isKitsTab && kits.length > this.previewLimit) {
        const viewAll = (window.mediaCenterStrings?.viewAllKits || 'View all {{ count }} media kits')
          .replace('{{ count }}', String(kits.length));
        html += `<div class="media-center-search-section__footer"><button type="button" class="media-center-search-view-all" data-mc-view-all-kits>${viewAll}</button></div>`;
      }

      if (isKitsTab) {
        html += this.renderPagination(totalPages);
      }

      html += `</section>`;
    }

    this.resultsEl.innerHTML = html;
    MediaCenter.hydrateKitCardSizes(this.resultsEl);
  }
}


class MediaCenterRouter {
  static get isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  static init() {
    const roots = document.querySelectorAll('[data-media-center-router]');
    if (!roots.length) return;

    const params = new URLSearchParams(window.location.search);
    const urlHandle = (params.get('handle') || '').trim().toLowerCase();
    const urlParent = (params.get('parent') || '').trim().toLowerCase();
    const designMode = MediaCenterRouter.isDesignMode;

    // Fast pass: hide non-matching sections before any heavier mount work.
    if (!designMode && (urlHandle || urlParent)) {
      roots.forEach((root) => {
        if (MediaCenterRouter.shouldHideSection(root, urlHandle, urlParent)) {
          const sectionEl = root.closest('.shopify-section') || root;
          sectionEl.hidden = true;
          root.classList.add('is-mounted');
        }
      });
    }

    roots.forEach((root) => {
      if (root.classList.contains('is-mounted') && root.closest('.shopify-section')?.hidden) {
        return;
      }
      MediaCenterRouter.mount(root);
    });
  }

  static shouldHideSection(root, urlHandle, urlParent) {
    const sectionHandle = (root.getAttribute('data-mc-section-handle') || '').trim().toLowerCase();
    const sectionParent = (root.getAttribute('data-mc-parent-handle') || '').trim().toLowerCase();
    const level = root.getAttribute('data-mc-level') || '';

    if (urlHandle && sectionHandle && sectionHandle !== urlHandle) {
      return true;
    }

    if (urlParent && sectionParent && sectionParent !== urlParent) {
      return true;
    }

    if (level === 'kit' && urlHandle && urlParent && sectionHandle === urlHandle && !sectionParent) {
      return Array.from(document.querySelectorAll('[data-media-center-router][data-mc-level="kit"]')).some(
        (el) =>
          el !== root &&
          (el.getAttribute('data-mc-section-handle') || '').trim().toLowerCase() === urlHandle &&
          (el.getAttribute('data-mc-parent-handle') || '').trim().toLowerCase() === urlParent
      );
    }

    return false;
  }

  static mount(root) {
    if (root.classList.contains('is-mounted') && root.closest('.shopify-section')?.hidden) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const sectionHandle = (root.getAttribute('data-mc-section-handle') || '').trim().toLowerCase();
    const sectionParent = (root.getAttribute('data-mc-parent-handle') || '').trim().toLowerCase();
    const urlHandle = (params.get('handle') || '').trim().toLowerCase();
    const urlParent = (params.get('parent') || '').trim().toLowerCase();
    const designMode = MediaCenterRouter.isDesignMode;

    // Multiple sections on one page: ?handle= filters to the matching section only.
    if (!designMode && urlHandle && sectionHandle && sectionHandle !== urlHandle) {
      const sectionEl = root.closest('.shopify-section') || root;
      sectionEl.hidden = true;
      root.classList.add('is-mounted');
      return;
    }

    // Same kit handle under different collections: ?parent= picks the clicked parent.
    if (!designMode && urlParent && sectionParent && sectionParent !== urlParent) {
      const sectionEl = root.closest('.shopify-section') || root;
      sectionEl.hidden = true;
      root.classList.add('is-mounted');
      return;
    }

    // Dedicated kit sections with the same handle: when parent is in the URL but this
    // section has no parent attr, still hide if another section on the page owns that parent.
    if (!designMode && urlHandle && urlParent && sectionHandle === urlHandle && !sectionParent) {
      const siblingMatch = Array.from(document.querySelectorAll('[data-media-center-router][data-mc-level="kit"]')).some(
        (el) =>
          el !== root &&
          (el.getAttribute('data-mc-section-handle') || '').trim().toLowerCase() === urlHandle &&
          (el.getAttribute('data-mc-parent-handle') || '').trim().toLowerCase() === urlParent
      );
      if (siblingMatch) {
        const sectionEl = root.closest('.shopify-section') || root;
        sectionEl.hidden = true;
        root.classList.add('is-mounted');
        return;
      }
    }

    const handle = urlHandle || sectionHandle;
    const isFixedEntry = Boolean(sectionHandle) && handle === sectionHandle;
    const level = root.getAttribute('data-mc-level') || '';
    const emptyEl = root.querySelector('[data-mc-empty]');
    const emptyChildrenEl = root.querySelector('[data-mc-empty-children]');
    const childrenSection = root.querySelector('[data-mc-children-section]');
    const titleEl = root.querySelector('[data-mc-title]');
    const descriptionEl = root.querySelector('[data-mc-description]');
    const breadcrumbEl = root.querySelector('[data-mc-breadcrumb-current]');
    const countEl = root.querySelector('[data-mc-count]');
    const handleHintEl = root.querySelector('[data-mc-handle-hint]');
    const availableHandlesEl = root.querySelector('[data-mc-available-handles]');

    const availableHandles = Array.from(root.querySelectorAll('[data-mc-entry]'))
      .map((el) => (el.getAttribute('data-mc-handle') || '').trim().toLowerCase())
      .filter(Boolean);

    const showHandleHelp = (requestedHandle) => {
      if (handleHintEl && requestedHandle) {
        handleHintEl.textContent = urlParent
          ? `No match for handle "${requestedHandle}" with parent "${urlParent}".`
          : `No match for handle "${requestedHandle}".`;
        handleHintEl.hidden = false;
      }
      if (availableHandlesEl && availableHandles.length > 0) {
        availableHandlesEl.textContent = `Available handles on this page: ${availableHandles.join(', ')}.`;
        availableHandlesEl.hidden = false;
      }
    };

    if (!handle) {
      if (emptyEl) emptyEl.hidden = false;
      if (childrenSection) childrenSection.hidden = true;
      showHandleHelp('');
      root.classList.add('is-mounted');
      return;
    }

    const entryCandidates = Array.from(root.querySelectorAll('[data-mc-entry]')).filter(
      (el) => (el.getAttribute('data-mc-handle') || '').toLowerCase() === handle
    );

    let entryTemplate = entryCandidates.find((el) => {
      if (!urlParent) return true;
      const entryParent = (el.getAttribute('data-mc-parent') || '').trim().toLowerCase();
      return entryParent === urlParent || entryParent === '';
    });

    if (!entryTemplate && entryCandidates.length === 1 && !urlParent) {
      entryTemplate = entryCandidates[0];
    }

    if (!entryTemplate && urlParent && sectionParent === urlParent && isFixedEntry) {
      entryTemplate = entryCandidates[0];
    }

    if (!entryTemplate) {
      if (emptyEl) emptyEl.hidden = false;
      if (childrenSection) childrenSection.hidden = true;
      showHandleHelp(handle);
      root.classList.add('is-mounted');
      return;
    }

    if (emptyEl) emptyEl.hidden = true;

    let entry = {};
    try {
      const jsonEl = entryTemplate.content.querySelector('script[type="application/json"]');
      entry = JSON.parse(jsonEl?.textContent || '{}');
    } catch (error) {
      entry = {};
    }

    if (titleEl) titleEl.textContent = entry.title || handle;
    if (breadcrumbEl) breadcrumbEl.textContent = entry.title || handle;
    if (!document.querySelector('[data-media-center-router]:not(.is-mounted)')) {
      document.title = `${entry.title || handle} · Media Center`;
    }

    if (descriptionEl) {
      if (entry.description) {
        descriptionEl.innerHTML = entry.description;
        descriptionEl.hidden = false;
      } else {
        descriptionEl.hidden = true;
        descriptionEl.innerHTML = '';
      }
    }

    const parentCrumb = root.querySelector('[data-mc-parent-crumb]');
    const parentLink = root.querySelector('[data-mc-parent-link]');
    const grandparentCrumb = root.querySelector('[data-mc-grandparent-crumb]');
    const grandparentLink = root.querySelector('[data-mc-grandparent-link]');

    if (parentCrumb && parentLink && entry.parentHandle && level !== 'kit') {
      parentCrumb.hidden = false;
      parentLink.textContent = entry.parentTitle || entry.parentHandle;
      if (level === 'collection') {
        parentLink.href = MediaCenterRouter.buildHandleUrl(root.getAttribute('data-mc-category-url'), entry.parentHandle);
      } else {
        parentLink.href = '#';
      }
    } else if (parentCrumb) {
      parentCrumb.hidden = true;
    }

    if (grandparentCrumb && grandparentLink && entry.grandparentHandle) {
      grandparentCrumb.hidden = false;
      grandparentLink.textContent = entry.grandparentTitle || entry.grandparentHandle;
      grandparentLink.href = MediaCenterRouter.buildHandleUrl(root.getAttribute('data-mc-category-url'), entry.grandparentHandle);
    } else if (grandparentCrumb) {
      grandparentCrumb.hidden = true;
    }

    MediaCenterRouter.dedupeBreadcrumbs(root, entry.title || handle);

    if (level === 'kit') {
      const statsEl = root.querySelector('[data-mc-stats]');
      const sizeWrap = root.querySelector('[data-mc-size-wrap]');
      const sizeEl = root.querySelector('[data-mc-size]');
      const zipEl = root.querySelector('[data-mc-zip]');

      if (zipEl) {
        if (entry.zipUrl) {
          zipEl.href = entry.zipUrl;
          zipEl.hidden = false;
        } else {
          zipEl.hidden = true;
        }
      }

      if (sizeWrap && sizeEl) {
        if (entry.totalSize) {
          sizeEl.textContent = entry.totalSize;
          sizeWrap.hidden = false;
        } else {
          sizeWrap.hidden = true;
        }
      }

      if (statsEl) statsEl.hidden = false;
    }

    let visibleCount = 0;
    root.querySelectorAll('[data-mc-child]').forEach((child) => {
      if (isFixedEntry) {
        child.hidden = false;
        visibleCount += 1;
        return;
      }

      const parent = (child.getAttribute('data-mc-parent') || '').toLowerCase();
      const match = parent === handle;
      child.hidden = !match;
      if (match) visibleCount += 1;
    });

    if (childrenSection) childrenSection.hidden = false;
    if (countEl) countEl.textContent = String(visibleCount);

    if (level === 'kit') {
      const assetLabel = root.querySelector('[data-mc-asset-label]');
      if (assetLabel) {
        assetLabel.textContent = visibleCount === 1 ? `${visibleCount} image` : `${visibleCount} images`;
      }
    }

    if (emptyChildrenEl) {
      emptyChildrenEl.hidden = visibleCount > 0;
    }

    if (visibleCount === 0 && level === 'kit') {
      const zipEl = root.querySelector('[data-mc-zip]');
      if (zipEl && !zipEl.hidden && emptyChildrenEl) {
        emptyChildrenEl.hidden = true;
      }
    }

    MediaCenter.bindAssetActions(root);
    MediaCenter.bindPreviewTriggers(root);
    MediaCenter.hydrateAssetFileSizes(root);
    root.classList.add('is-mounted');
  }

  static buildHandleUrl(base, handle) {
    if (!base || !handle) return '#';
    const join = base.includes('?') ? '&' : '?';
    return `${base}${join}handle=${encodeURIComponent(handle)}`;
  }

  static normalizeBreadcrumbLabel(value) {
    return (value || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
  }

  static dedupeBreadcrumbs(root, currentLabel) {
    const current = MediaCenterRouter.normalizeBreadcrumbLabel(currentLabel);
    if (!current) return;

    const crumbSelectors = ['[data-mc-parent-crumb]', '[data-mc-grandparent-crumb]'];
    crumbSelectors.forEach((selector) => {
      const crumb = root.querySelector(selector);
      if (!crumb || crumb.hidden) return;

      const link = crumb.querySelector('a');
      const label = MediaCenterRouter.normalizeBreadcrumbLabel(link?.textContent || '');
      if (label && label === current) {
        crumb.hidden = true;
      }
    });
  }
}

class MediaCenter {
  static init() {
    MediaCenterRouter.init();

    const runDeferred = () => {
      document.querySelectorAll('[data-media-center-search-root]').forEach((root) => {
        new MediaCenterSearch(root);
      });

      MediaCenter.hydrateCollectionCounts();
      MediaCenter.hydrateKitCardSizes(document);
      MediaCenter.hydrateAssetFileSizes(document);
      MediaCenter.bindAssetActions(document);
      MediaCenter.bindPreviewTriggers(document);
      MediaCenter.bindVideoModal(document);
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(runDeferred, { timeout: 400 });
    } else {
      window.setTimeout(runDeferred, 0);
    }
  }

  static formatBytes(bytes) {
    const value = Number(bytes);
    if (!Number.isFinite(value) || value <= 0) return '';

    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = value;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex += 1;
    }

    let rounded;
    if (unitIndex === 0) {
      rounded = String(Math.round(size));
    } else if (size >= 100) {
      rounded = size.toFixed(0);
    } else if (size >= 10) {
      rounded = size.toFixed(1);
    } else {
      rounded = size.toFixed(2);
    }

    return `${rounded} ${units[unitIndex]}`;
  }

  static async getRemoteFileSize(url) {
    if (!url) return '';

    MediaCenter._fileSizeCache ||= new Map();
    if (MediaCenter._fileSizeCache.has(url)) {
      return MediaCenter._fileSizeCache.get(url);
    }

    const parseSize = (response) => {
      const contentLength = response.headers.get('content-length');
      if (contentLength) return MediaCenter.formatBytes(contentLength);

      const contentRange = response.headers.get('content-range');
      const match = contentRange && contentRange.match(/\/(\d+)\s*$/);
      if (match) return MediaCenter.formatBytes(match[1]);

      return '';
    };

    let formatted = '';

    try {
      const headResponse = await fetch(url, { method: 'HEAD', mode: 'cors' });
      formatted = parseSize(headResponse);
    } catch (error) {
      formatted = '';
    }

    if (!formatted) {
      const controller = new AbortController();
      try {
        const rangeResponse = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          headers: { Range: 'bytes=0-0' },
          signal: controller.signal
        });
        formatted = parseSize(rangeResponse);
      } catch (error) {
        formatted = '';
      } finally {
        controller.abort();
      }
    }

    MediaCenter._fileSizeCache.set(url, formatted);
    return formatted;
  }

  static async hydrateKitCardSizes(scope = document) {
    const sizeEls = Array.from(scope.querySelectorAll('[data-mc-kit-size]'));

    sizeEls.forEach(async (el) => {
      if (el.textContent.trim()) {
        el.hidden = false;
        return;
      }

      const zipUrl = el.getAttribute('data-mc-zip-url');
      if (!zipUrl) {
        el.hidden = true;
        return;
      }

      const formatted = await MediaCenter.getRemoteFileSize(zipUrl);
      if (formatted) {
        el.textContent = formatted;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });
  }

  static async hydrateAssetFileSizes(scope = document) {
    const sizeEls = Array.from(scope.querySelectorAll('[data-mc-asset-size][data-mc-file-url]'));

    sizeEls.forEach(async (el) => {
      // Always prefer real file size from the download URL.
      const fileUrl = el.getAttribute('data-mc-file-url');
      if (!fileUrl) {
        if (!el.textContent.trim()) el.hidden = true;
        return;
      }

      const formatted = await MediaCenter.getRemoteFileSize(fileUrl);
      if (formatted) {
        el.textContent = formatted;
        el.hidden = false;
      } else if (!el.textContent.trim()) {
        el.hidden = true;
      } else {
        el.hidden = false;
      }
    });
  }

  static async fetchDocument(url) {
    MediaCenter._documentCache ||= new Map();

    const cacheKey = String(url);
    if (MediaCenter._documentCache.has(cacheKey)) {
      return MediaCenter._documentCache.get(cacheKey);
    }

    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}`);
    }

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    MediaCenter._documentCache.set(cacheKey, doc);
    return doc;
  }

  static updateCardCount(scope, count, type = 'media_kits') {
    const meta = scope.querySelector('[data-mc-card-meta]');
    const text = scope.querySelector('[data-mc-card-count-text]');
    if (!meta || !text) return;

    meta.dataset.mcCardCount = String(count);

    if (type === 'collections') {
      text.textContent = count === 1 ? `${count} collection` : `${count} collections`;
      return;
    }

    text.textContent = count === 1 ? `${count} media kit` : `${count} media kits`;
  }

  static async getCollectionCount(url, handle) {
    if (!url || !handle) return 0;

    const doc = await MediaCenter.fetchDocument(url);
    const root = doc.querySelector(
      `[data-media-center-router][data-mc-level="collection"][data-mc-section-handle="${CSS.escape(handle)}"]`
    );

    if (root) {
      return Number(root.getAttribute('data-mc-entry-count') || 0);
    }

    const routerRoot = doc.querySelector('[data-media-center-router][data-mc-level="collection"]:not([data-mc-section-handle])');
    if (!routerRoot) return 0;

    const entry = routerRoot.querySelector(`[data-mc-entry][data-mc-handle="${CSS.escape(handle)}"]`);
    if (!entry) return 0;

    return routerRoot.querySelectorAll(`[data-mc-child][data-mc-parent="${CSS.escape(handle)}"]`).length;
  }

  static async getCategoryKitTotal(url, handle) {
    if (!url || !handle) return 0;

    const doc = await MediaCenter.fetchDocument(url);
    const root = doc.querySelector(
      `[data-media-center-router][data-mc-level="category"][data-mc-section-handle="${CSS.escape(handle)}"]`
    );

    if (!root) return 0;

    const collectionItems = Array.from(root.querySelectorAll('[data-mc-collection-handle][data-mc-collection-url]'));
    const counts = await Promise.all(
      collectionItems.map((item) =>
        MediaCenter.getCollectionCount(
          item.getAttribute('data-mc-collection-url'),
          item.getAttribute('data-mc-collection-handle')
        )
      )
    );

    return counts.reduce((sum, count) => sum + count, 0);
  }

  static hydrateCollectionCounts() {
    const collectionItems = Array.from(document.querySelectorAll('[data-mc-collection-handle][data-mc-collection-url]'));
    collectionItems.forEach(async (item) => {
      try {
        const count = await MediaCenter.getCollectionCount(
          item.getAttribute('data-mc-collection-url'),
          item.getAttribute('data-mc-collection-handle')
        );
        MediaCenter.updateCardCount(item, count, 'media_kits');
      } catch (error) {
        // Keep authored fallback count when related page cannot be loaded.
      }
    });

    const categoryItems = Array.from(document.querySelectorAll('[data-mc-landing-category][data-mc-category-handle][data-mc-category-url]'));
    categoryItems.forEach(async (item) => {
      try {
        const count = await MediaCenter.getCategoryKitTotal(
          item.getAttribute('data-mc-category-url'),
          item.getAttribute('data-mc-category-handle')
        );
        MediaCenter.updateCardCount(item, count, 'media_kits');
      } catch (error) {
        // Keep authored fallback count when related page cannot be loaded.
      }
    });

    const landingCollectionItems = Array.from(
      document.querySelectorAll('[data-mc-landing-collection][data-mc-collection-handle][data-mc-collection-url]')
    );
    landingCollectionItems.forEach(async (item) => {
      try {
        const count = await MediaCenter.getCollectionCount(
          item.getAttribute('data-mc-collection-url'),
          item.getAttribute('data-mc-collection-handle')
        );
        MediaCenter.updateCardCount(item, count, 'media_kits');
      } catch (error) {
        // Keep authored fallback count when related page cannot be loaded.
      }
    });
  }

  static bindAssetActions(scope) {
    scope.querySelectorAll('[data-copy-link]').forEach((button) => {
      if (button.dataset.bound) {
        return;
      }

      button.dataset.bound = 'true';
      button.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const url = button.getAttribute('data-copy-link');
        if (!url) {
          return;
        }

        const successMessage =
          window.mediaCenterStrings?.linkCopied || 'Link copied to your clipboard.';
        const failureMessage =
          window.mediaCenterStrings?.copyFailed || 'Could not copy link';

        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(url);
          } else {
            const input = document.createElement('input');
            input.value = url;
            input.setAttribute('readonly', '');
            input.style.position = 'absolute';
            input.style.left = '-9999px';
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
          }

          window.alert(successMessage);
        } catch (error) {
          window.alert(failureMessage);
        }
      });
    });
  }

  static bindPreviewTriggers(scope) {
    scope.querySelectorAll('[data-media-center-preview]').forEach((trigger) => {
      if (trigger.dataset.bound) {
        return;
      }

      trigger.dataset.bound = 'true';
      trigger.addEventListener('click', (event) => {
        const type = trigger.getAttribute('data-media-center-preview');
        const src = trigger.getAttribute('data-preview-src');

        if (!src) {
          return;
        }

        if (type === 'image') {
          event.preventDefault();
          MediaCenter.openImagePreview(src, trigger.getAttribute('data-preview-alt') || '');
        }

        if (type === 'video') {
          event.preventDefault();
          MediaCenter.openVideoModal(src);
        }
      });
    });
  }

  static bindVideoModal(scope) {
    const modal = scope.querySelector('[data-media-center-video-modal]');
    if (!modal || modal.dataset.bound) {
      return;
    }

    modal.dataset.bound = 'true';
    const closeButton = modal.querySelector('[data-media-center-video-close]');
    const video = modal.querySelector('video');

    const close = () => {
      modal.hidden = true;
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    };

    closeButton?.addEventListener('click', close);
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        close();
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) {
        close();
      }
    });

    MediaCenter.openVideoModal = (src) => {
      if (!video) {
        return;
      }

      video.src = src;
      modal.hidden = false;
      video.play().catch(() => {});
    };
  }

  static openImagePreview(src, alt) {
    const overlay = document.createElement('div');
    overlay.className = 'media-center-video-modal';
    overlay.innerHTML = `
      <div class="media-center-video-modal__dialog">
        <button type="button" class="media-center-video-modal__close" aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m1 1 14 14M1 15 15 1" stroke="currentColor"/></svg>
        </button>
        <img src="${src}" alt="${alt.replace(/"/g, '&quot;')}" style="width:100%;height:auto;display:block;background:#fff">
      </div>
    `;

    const close = () => overlay.remove();
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay || event.target.closest('.media-center-video-modal__close')) {
        close();
      }
    });

    document.addEventListener('keydown', function onKeydown(event) {
      if (event.key === 'Escape') {
        close();
        document.removeEventListener('keydown', onKeydown);
      }
    });

    document.body.appendChild(overlay);
  }

  static showToast(message) {
    let toast = document.querySelector('.media-center-toast');

    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'media-center-toast';
      document.body.appendChild(toast);
    }

    toast.textContent = message;
    toast.classList.add('is-visible');

    window.clearTimeout(toast._timeout);
    toast._timeout = window.setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 2200);
  }
}

const bootMediaCenter = () => {
  if (window.__mediaCenterBooted) return;
  window.__mediaCenterBooted = true;
  MediaCenter.init();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootMediaCenter, { once: true });
} else {
  bootMediaCenter();
}
